import { AxiosProgressEvent } from "axios";
import React from "react";
import { useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import ElasticService from "src/services/elasticService";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { useUploadFileMutation } from "@dashboard/files/api";
import { useUploadContext } from "@dashboard/files/context/UploadContext";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { useCurrentWorkspaceFeatures } from "../../workspaces/api/helpers";

// 256 MB in bytes
export const maxFileSize = 256 * 1024 * 1024;

const lowAvailableSlotsThreshold = 3;

export const forbiddenFileTypes = [
    "apk+",
    "apkm",
    "apkx",
    "deb",
    "exe",
    "gif",
    "htm",
    "html",
    "jpeg",
    "jpg",
    "mp3",
    "png",
    "ppt",
    "pptx",
];

type UseFileUploadOptions = {
    groupId?: number;
    onUploadStarted?: () => void;
};

export const useFileUpload = ({ groupId, onUploadStarted }: UseFileUploadOptions = {}) => {
    const [validationError, setValidationError] = React.useState<string | null>(null);

    const { t } = useTranslation(["common"]);
    const { abortController, setUploadStarted, setUploadProgress, setUploadedFileIds, setSelectedFiles } = useUploadContext();
    const [uploadFile] = useUploadFileMutation();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const user = AuthService.getCurrentUser();

    const { data: workspaceFeatures = [] } = useCurrentWorkspaceFeatures();
    const fileCountLimit = workspaceFeatures.find((item) => item.feature === "FileCountLimit");
    const currentFileCount = fileCountLimit?.used ?? 0;

    const maxFileCount = fileCountLimit?.max ?? Infinity;
    const availableSlots = maxFileCount - currentFileCount;

    const isLimitReached = availableSlots <= 0;
    const isLowOnSlots = availableSlots > 0 && availableSlots <= lowAvailableSlotsThreshold;

    const validateFiles = (selectedFiles: File[]) => {
        if (selectedFiles.length > availableSlots) {
            setValidationError(t("common:section.uploadField.fileCountLimitReached"));
            return false;
        }

        for (const file of selectedFiles) {
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            if (forbiddenFileTypes.includes(fileExtension || "")) {
                setValidationError(t("common:section.uploadField.unsupportedFileType"));
                return false;
            }
        }

        for (const file of selectedFiles) {
            if (file.size > maxFileSize) {
                setValidationError(t("common:section.uploadField.maxSizeExceeded"));
                return false;
            }
        }

        return true;
    };

    const uploadFiles = (selectedFiles: File[]) => {
        if (!selectedFiles.length || !workspaceId) {
            return;
        }

        setUploadStarted(true);
        onUploadStarted?.();
        let completedFiles = 0;
        const progressPerFile = selectedFiles.length === 1 ? 0 : 100 / selectedFiles.length;

        Promise.all(
            selectedFiles.map((file) => {
                if (abortController.signal.aborted) {
                    throw new Error("Upload canceled");
                }

                return uploadFile({
                    workspaceId,
                    filename: file.name,
                    fileToUpload: file,
                    controller: abortController,
                    groupId: groupId || null,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        if (selectedFiles.length === 1 && progressEvent.total) {
                            const fileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                            setUploadProgress(fileProgress);
                        } else {
                            const currentProgress = Math.round(completedFiles * progressPerFile);
                            setUploadProgress(currentProgress);
                        }
                    },
                    setUploadedFileIds,
                })
                    .unwrap()
                    .then(() => {
                        completedFiles++;
                        if (selectedFiles.length > 1) {
                            const newProgress = Math.round(completedFiles * progressPerFile);
                            setUploadProgress(newProgress);
                        }
                        if (!abortController.signal.aborted && user?.email) {
                            showSuccessToast(t("common:section.uploadField.toast.fileUploadedSuccess"));
                            ElasticService.createTransaction("upload", {
                                user_email: user.email,
                                file_name: file.name,
                                file_size: file.size,
                            });
                        }
                    });
            }),
        )
            .catch(() => {
                // Error toast is shown in files API upload mutation (rtkClient).
            })
            .finally(() => {
                setUploadProgress(0);
                setUploadStarted(false);
                setSelectedFiles([]);
            });
    };

    const onFilesSelected = (selectedFiles: File[]) => {
        if (!selectedFiles?.length) {
            return;
        }

        setValidationError(null);

        if (validateFiles(selectedFiles)) {
            setSelectedFiles(selectedFiles);
            uploadFiles(selectedFiles);
        }
    };

    const clearValidationError = () => {
        setValidationError(null);
    };

    return { onFilesSelected, availableSlots, isLimitReached, isLowOnSlots, validationError, clearValidationError };
};
